import React from 'react';
import type { LucideIcon } from 'lucide-react';

interface EmptyStateProps {
    icon?: LucideIcon;
    title: string;
    description?: string;
    action?: {
        label: string;
        onClick: () => void;
    };
    secondaryAction?: {
        label: string;
        onClick: () => void;
    };
}

/**
 * Premium Empty State Component
 * 
 * Displays a beautiful, engaging empty state UI when no data is available.
 * Follows the design system with subtle glassmorphism and gradient accents.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
    icon: Icon,
    title,
    description,
    action,
    secondaryAction
}) => {
    return (
        <div className="relative flex flex-col items-center justify-center p-12 md:p-20 text-center  animate-in fade-in-scale duration-500">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 via-white to-gray-50/50 rounded-2xl -z-10" />

            {/* Icon Container with Gradient */}
            {Icon && (
                <div className="relative mb-6 group">
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-100 rounded-full blur-xl opacity-50 group-hover:opacity-70 transition-opacity" />

                    {/* Icon background */}
                    <div className="relative w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-sm">
                        <Icon className="w-10 h-10 text-gray-400" strokeWidth={1.5} />
                    </div>
                </div>
            )}

            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">
                {title}
            </h3>

            {/* Description */}
            {description && (
                <p className="text-gray-500 max-w-md mb-8 leading-relaxed">
                    {description}
                </p>
            )}

            {/* Actions */}
            {(action || secondaryAction) && (
                <div className="flex flex-col sm:flex-row gap-3 items-center">
                    {action && (
                        <button
                            onClick={action.onClick}
                            className="group relative px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold overflow-hidden shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-300 hover:scale-105 active:scale-95"
                        >
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                            <span className="relative z-10 flex items-center gap-2">
                                {action.label}
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </span>
                        </button>
                    )}

                    {secondaryAction && (
                        <button
                            onClick={secondaryAction.onClick}
                            className="px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 hover:scale-105 active:scale-95 shadow-sm"
                        >
                            {secondaryAction.label}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmptyState;
